'use client'

import { alert } from '@repo/ui/Alert'
import { CloseIcon } from '@repo/ui/CloseIcon'
import { confirm } from '@repo/ui/Confirm'
// import { EmailImportanceControl } from '@repo/ui/EmailImportanceControl'
import { EmailSidebar } from '@repo/ui/EmailSidebar'
import {
  FormatBold,
  FormatItalic,
  FormatListBulleted,
  FormatListNumbered,
  FormatUnderlined,
  FormatAlignCenter,
  FormatAlignLeft,
  FormatAlignRight,
  FormatStrikethrough,
  BorderColor as FormatHighlight,
  Link as FormatLink,
  TableChart,
  Subscript as FormatSubscript,
  Superscript as FormatSuperscript,
  Redo,
  Undo
} from '@mui/icons-material'
import { formatBytes } from '@repo/utils/numbers'
import { LoadingState } from '@repo/ui/LoadingState'
import { Modal } from '@repo/ui/Modal'
import { PaperClipIcon } from '@repo/ui/PaperClipIcon'
import { RecipientsInput } from '@repo/ui/RecipientsInput'
import { SendIcon } from '@repo/ui/SendIcon'
import { TableKit } from '@tiptap/extension-table'
import { TextStyleKit } from '@tiptap/extension-text-style'
import { useAuthenticationStore } from '@repo/store/useAuthenticationStore'
import { useEditor, EditorContent } from '@tiptap/react'
import { useEffect, useState, useRef, useCallback } from 'react'
import { useEmailCardStore } from '@repo/store/useEmailCardStore'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { useMattersStore } from '@repo/store/useMattersStore'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'
import Highlight from '@tiptap/extension-highlight'
import Link from '@tiptap/extension-link'
import MUIButton from '@mui/material/Button'
import MUICheckbox from '@mui/material/Checkbox'
import MUIDivider from '@mui/material/Divider'
import MUIFormControlLabel from '@mui/material/FormControlLabel'
import MUIIconButton from '@mui/material/IconButton'
import MUIInput from '@mui/material/TextField'
import MUIInputLabel from '@mui/material/InputLabel'
import MUIMenuItem from '@mui/material/MenuItem'
import MUIPaper from '@mui/material/Paper'
import MUIPopover from '@mui/material/Popover'
import MUISelect from '@mui/material/Select'
import MUIStack from '@mui/material/Stack'
import MUITooltip from '@mui/material/Tooltip'
import MUITypography from '@mui/material/Typography'
import StarterKit from '@tiptap/starter-kit'
import Strike from '@tiptap/extension-strike'
import Subscript from '@tiptap/extension-subscript'
import Superscript from '@tiptap/extension-superscript'
import Text from '@tiptap/extension-text'
import TextAlign from '@tiptap/extension-text-align'
import styles from './EmailCompositionPanel.module.scss'

type EmailCompositionPanelProps = {
  testData?: boolean
}

type RecipientKind = 'to' | 'cc' | 'bcc'

type DeviceAttachment = {
  id: string
  name: string
  size: number
  type: string
  lastModified: number
  file: File
}

// Utility to parse JSON or fallback to HTML
function parseEditorContent(input: string | object) {
  try {
    if (typeof input === 'string') {
      const parsed = JSON.parse(input)
      if (parsed && parsed.type === 'doc') return parsed
      return input
    }
    return input
  } catch {
    return input
  }
}

export const EmailCompositionPanel = ({ testData = false }: EmailCompositionPanelProps) => {
  const theme = useTheme()
  const navigate = useNavigate()

  const [bccValid, setBccValid] = useState(true)
  const [bodyValid, setBodyValid] = useState(false)
  const [ccValid, setCcValid] = useState(true)
  const [content, setContent] = useState<string | object>('')
  const [flagged, setFlagged] = useState<'normal' | 'low' | 'high' | 'displayNormal' | 'displayHigh' | 'displayLow'>(
    'normal'
  )
  const [deviceAttachments, setDeviceAttachments] = useState<DeviceAttachment[]>([])
  const [emailType, setEmailType] = useState<number>(0)
  const [inputBccValue, setInputBccValue] = useState('')
  const [inputCcValue, setInputCcValue] = useState('')
  const [inputToValue, setInputToValue] = useState('')
  const [isDragging, setIsDragging] = useState(false)
  const [linkAnchorEl, setLinkAnchorEl] = useState<null | HTMLElement>(null)
  const [linkNewTab, setLinkNewTab] = useState<boolean>(true)
  const [linkText, setLinkText] = useState<string>('')
  const [linkUrl, setLinkUrl] = useState<string>('')
  const [lockMatterSelection, setLockMatterSelection] = useState<boolean>(false)
  const [refreshMatterHistory, setRefreshMatterHistory] = useState<boolean>(false)
  const [subject, setSubject] = useState('')
  const [suppressHeader, setSuppressHeader] = useState<boolean>(true)
  const [tableAnchorEl, setTableAnchorEl] = useState<null | HTMLElement>(null)
  const [tableCols, setTableCols] = useState<number>(3)
  const [tableHeaderRow, setTableHeaderRow] = useState<boolean>(true)
  const [tableRows, setTableRows] = useState<number>(3)
  const [toValid, setToValid] = useState(false)

  const [isSendingEmail, setIsSendingEmail] = useState(false)
  const [showMatterHistorySettings, setShowMatterHistorySettings] = useState(true)

  const dragDepth = useRef(0)
  const dropZoneRef = useRef<HTMLDivElement | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const previousClientMatterNoRef = useRef<string | null>(null)

  const fileKey = (f: File) => `${f.name}-${f.size}-${f.lastModified}`
  const fileToAttachment = (f: File): DeviceAttachment => ({
    id: `${fileKey(f)}-${(globalThis.crypto as any)?.randomUUID?.() ?? Math.random().toString(36).slice(2)}`,
    name: f.name,
    size: f.size,
    type: f.type,
    lastModified: f.lastModified,
    file: f
  })
  const addFiles = (files: File[]) => {
    if (!files?.length) return
    setDeviceAttachments((prev) => {
      const existing = new Set(prev.map((p) => fileKey(p.file)))
      const fresh = files.filter((f) => !existing.has(fileKey(f))).map(fileToAttachment)
      return [...prev, ...fresh]
    })
  }

  const handleDragEnter: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault()
    e.stopPropagation()
    dragDepth.current += 1
    setIsDragging(true)
  }

  const handleDragOver: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDragLeave: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault()
    e.stopPropagation()
    dragDepth.current = Math.max(0, dragDepth.current - 1)
    if (dragDepth.current === 0) setIsDragging(false)
  }

  const handleDrop: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault()
    e.stopPropagation()
    dragDepth.current = 0
    setIsDragging(false)
    const files = Array.from(e.dataTransfer?.files ?? [])
    addFiles(files)
  }

  useEffect(() => {
    const prevent = (e: DragEvent) => e.preventDefault()
    window.addEventListener('dragover', prevent)
    window.addEventListener('drop', prevent)
    return () => {
      window.removeEventListener('dragover', prevent)
      window.removeEventListener('drop', prevent)
    }
  }, [])

  const escapeHtml = (s: unknown): string => {
    const str = String(s ?? '')
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }

  const buildHeaderHTML = (details?: any) => {
    if (!details || !details.from) return ''
    const when = details?.date ? new Date(details.date).toLocaleString() : ''
    const fromDisplay = details?.from?.displayName || details?.from?.emailAdresses || ''
    const fromEmail = details?.from?.emailAdresses || details?.from?.displayName || ''
    if (!when && !fromDisplay && !fromEmail) return ''
    return `<p><span style="color: rgb(61, 81, 123);">On ${escapeHtml(when)}, ${escapeHtml(
      fromDisplay
    )} &lt;${escapeHtml(fromEmail)}&gt;, wrote:</span></p>`
  }

  const { graphApiEmailAddress, getProfile } = useAuthenticationStore()
  const {
    attachedAttachments,
    bccRecipients,
    ccRecipients,
    clearAttachmentsAndPackages,
    emailDetailsFromOsprey1,
    emailModalOpen: open,
    emailSendingCurrentMatterData,
    fetchAllMatterEmailConversationsByInternetMessageIds,
    fetchAttachments,
    fetchEmailDetailsFromOsprey1,
    mergedEmailTemplate,
    mostRecentlySelectedPackage,
    removeAttachedAttachment,
    removeAttachedPackage,
    removeBccRecipient,
    removeCcRecipient,
    removeToRecipient,
    reset: resetEmailCardStore,
    selectedEmailTemplatePosition,
    sendEmailWithAttachments,
    setBccRecipients,
    setCcRecipients,
    setEmailModalOpen: setOpen,
    setEmailSendingCurrentMatterData,
    setToRecipients,
    toggleSetAttachedAttachments,
    toRecipients
  } = useEmailCardStore()
  const { fetchMatter, matters } = useMattersStore()

  const openTablePopover = Boolean(tableAnchorEl)
  const closeTablePopover = () => setTableAnchorEl(null)

  const openLinkPopover = Boolean(linkAnchorEl)
  const closeLinkPopover = () => setLinkAnchorEl(null)

  const subjectValid = subject.trim().length > 0
  const matterValid = !!emailSendingCurrentMatterData?.clientMatterNo
  const canSend = toValid && ccValid && bccValid && subjectValid && bodyValid && matterValid

  const handleValidity = ({ field, valid }: { field: 'to' | 'cc' | 'bcc'; valid: boolean }) => {
    if (field === 'to') setToValid(valid)
    else if (field === 'cc') setCcValid(valid)
    else if (field === 'bcc') setBccValid(valid)
  }

  const normalizeUrl = (u: string) => {
    try {
      const parsed = u.includes('://') ? new URL(u) : new URL(`https://${u}`)
      return parsed.href
    } catch {
      return u
    }
  }

  const handleInsertTable = () => {
    if (!editor) return
    const rows = Math.max(1, Math.min(50, Number(tableRows) || 1))
    const cols = Math.max(1, Math.min(50, Number(tableCols) || 1))

    editor.chain().focus().insertTable({ rows, cols, withHeaderRow: tableHeaderRow }).run()

    closeTablePopover()
  }

  const handleApplyLink = () => {
    if (!editor) return
    const rawUrl = linkUrl.trim()
    const text = linkText.trim()
    if (!rawUrl) return

    const href = normalizeUrl(rawUrl)
    const target = linkNewTab ? '_blank' : null
    const rel = linkNewTab ? 'noopener noreferrer' : undefined

    const { from, to } = editor.state.selection
    const hasSelection = from !== to

    const chain = editor.chain().focus()

    if (text) {
      chain
        .insertContent({
          type: 'text',
          text,
          marks: [{ type: 'link', attrs: { href, target, rel } }]
        })
        .run()
    } else if (hasSelection) {
      chain.extendMarkRange('link').setLink({ href, target, rel }).run()
    } else {
      chain
        .insertContent({
          type: 'text',
          text: rawUrl,
          marks: [{ type: 'link', attrs: { href, target, rel } }]
        })
        .run()
    }

    setLinkText('')
    setLinkUrl('')
    setLinkNewTab(true)
    closeLinkPopover()
  }

  const editor = useEditor({
    extensions: [
      StarterKit,
      TextAlign.configure({
        types: ['heading', 'paragraph']
      }),
      Text,
      TextStyleKit,
      Highlight.configure({ multicolor: true }),
      Strike,
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: 'https',
        protocols: ['http', 'https'],
        HTMLAttributes: {
          target: null,
          rel: 'noopener noreferrer'
        },
        isAllowedUri: (url, ctx) => {
          try {
            const parsedUrl = url.includes(':') ? new URL(url) : new URL(`${ctx.defaultProtocol}://${url}`)
            if (!ctx.defaultValidate(parsedUrl.href)) return false
            const disallowedProtocols = ['ftp', 'file', 'mailto']
            const protocol = parsedUrl.protocol.replace(':', '')
            if (disallowedProtocols.includes(protocol)) return false
            const allowed = ctx.protocols.map((p) => (typeof p === 'string' ? p : p.scheme))
            if (!allowed.includes(protocol)) return false
            const disallowedDomains = ['example-phishing.com', 'malicious-site.net']
            if (disallowedDomains.includes(parsedUrl.hostname)) return false
            return true
          } catch {
            return false
          }
        },
        shouldAutoLink: (url) => {
          try {
            const parsedUrl = url.includes(':') ? new URL(url) : new URL(`https://${url}`)
            const disallowed = ['example-no-autolink.com', 'another-no-autolink.com']
            return !disallowed.includes(parsedUrl.hostname)
          } catch {
            return false
          }
        }
      }),
      Subscript,
      Superscript,
      TableKit.configure({
        table: { resizable: true }
      })
    ],
    content: parseEditorContent(content),
    onUpdate: ({ editor }) => {
      setContent(editor.getHTML())
      const hasText = editor.state.doc.textContent.replace(/\s|\u00A0/g, '').length > 0
      if (hasText) {
        setBodyValid(true)
        return
      }

      const json: any = editor.getJSON()
      const hasStructural = (node: any): boolean => {
        if (!node) return false
        if (node.type === 'table') return true
        if (Array.isArray(node.content)) return node.content.some(hasStructural)
        return false
      }
      setBodyValid(hasStructural(json))
    }
  })

  useEffect(() => {
    if (!editor || !mergedEmailTemplate) return

    const mergedTemplate = mergedEmailTemplate.mergedBody
    let templateHTML = ''

    if (typeof mergedTemplate === 'string') {
      templateHTML = mergedTemplate
    } else {
      const snapshot = editor.getHTML()
      editor.commands.setContent(mergedTemplate as any)
      templateHTML = editor.getHTML()
      editor.commands.setContent(snapshot)
    }

    // init the variable that will become the new HTML for the editor
    let finalHTML: string = ''

    // get existing HTML from the text editor
    let html = editor.getHTML()

    // clean up any blank markup
    const blankHtmlPatterns = ['<br>', '<p><br></p>', '<p></p>']
    blankHtmlPatterns.forEach((pattern) => {
      if (html.startsWith(pattern)) {
        html = html.replace(pattern, '')
      }
    })

    // previousEmailIntro is the text which precedes the previous email chain, if there is one
    const previousEmailIntro = emailDetailsFromOsprey1 ? buildHeaderHTML(emailDetailsFromOsprey1) : null
    const idx = previousEmailIntro ? html.indexOf(previousEmailIntro) : -1
    const hasAPreviousEmailThread = idx !== -1
    const existingHTML = hasAPreviousEmailThread ? html.slice(0, idx) : html
    const previousEmailThread = hasAPreviousEmailThread ? '<p></p>' + html.slice(idx) : ''

    if (selectedEmailTemplatePosition === 'replace') {
      finalHTML = `${templateHTML}${previousEmailThread}`
    } else if (selectedEmailTemplatePosition === 'append') {
      finalHTML = `${existingHTML}${templateHTML}${previousEmailThread}`
    }

    editor.commands.setContent(finalHTML)
    setContent(finalHTML)
    setSubject(mergedEmailTemplate.mergedSubject || '')
    useEmailCardStore.setState({ mergedEmailTemplate: null })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, mergedEmailTemplate, emailDetailsFromOsprey1])

  const handleClose = async (opts?: { skipConfirm?: boolean }) => {
    if (!opts?.skipConfirm) {
      const proceed = await confirm('Are you sure you want to close this email? You will lose any unsaved changes.')
      if (!proceed) return
    }

    resetEmailCardStore()
    clearAttachmentsAndPackages()
    setSubject('')
    setContent('')
    setInputToValue('')
    setInputCcValue('')
    setInputBccValue('')
    setDeviceAttachments([])
    setEmailType(0)
    setSuppressHeader(true)
    setLockMatterSelection(false)
    setOpen({ isOpen: false })
    editor?.commands.clearContent()
  }

  const checkForGraphAPIToken = async () => {
    const { hasGraphApiTokens, hasGraphApiSubscriptions } = await getProfile()

    if (hasGraphApiTokens !== true || hasGraphApiSubscriptions !== true) {
      const proceed = await confirm(
        `To send emails, we need to configure your Microsoft account. Would you like to do this now?`
      )

      if (!proceed) {
        handleClose({ skipConfirm: true })
        return
      }

      navigate('configuration/microsoft/email-setup')
      handleClose({ skipConfirm: true })
    }
  }

  const handleSendEmail = async () => {
    if (testData) return

    const isMatterHistory = window.location.href.includes('matter-history')

    if (canSend) {
      setIsSendingEmail(true)

      try {
        await sendEmailWithAttachments({
          emailAddress: graphApiEmailAddress,
          subject,
          body: content,
          attachments: attachedAttachments.length === 0 ? null : attachedAttachments,
          deviceAttachments: deviceAttachments.length === 0 ? null : deviceAttachments,
          sendEmailAction: emailType,
          inReplyToInternetMessageId: emailDetailsFromOsprey1?.internetMessageId
        })
        handleClose({ skipConfirm: true })
        isMatterHistory && setRefreshMatterHistory(true)
      } catch (error: any) {
        setIsSendingEmail(false)

        if (error?.status === 498 || error?.status === 424) {
          alert(
            <MUIStack>
              <MUITypography>Your Microsoft Graph API token has expired.</MUITypography>
              <MUITypography>
                <a
                  href="/#/configuration/microsoft/email-setup"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#1976d2', textDecoration: 'underline' }}
                >
                  Click here to refresh your token to continue. (This will be opened in a new tab)
                </a>
              </MUITypography>
            </MUIStack>
          )
          return
        } else if (error.status === 413) {
          alert('The email is too large to send. Please reduce the size of email and try again.')
          return
        } else {
          alert(
            <MUIStack>
              <MUITypography>There has been an error sending this Email</MUITypography>
              <MUITypography>
                {error?.message ?? 'Please try again or contact support if the issue persists.'}
              </MUITypography>
            </MUIStack>
          )
          console.log('Error:', error)
        }
      } finally {
        setIsSendingEmail(false)
      }
    } else {
      let alertMessage = []

      alertMessage.push(
        <MUITypography key="alert-message" variant="body1" sx={{ mb: 2 }}>
          Please ensure all required fields are filled out correctly before sending:
          <ul>
            {(!toValid || !ccValid || !bccValid) && <li>Valid recipients</li>}
            {!subjectValid && <li>A subject</li>}
            {!bodyValid && <li>An email body</li>}
            {!matterValid && <li>A matter selected</li>}
          </ul>
        </MUITypography>
      )

      alert(alertMessage)
    }
  }

  useEffect(() => {
    if (!refreshMatterHistory) return

    const onMessageRefreshMatterHistory = async () => {
      const iframe = document.querySelector('iframe[name="ospreyIframe"]')
      // @ts-ignore
      const iframeDocument = iframe?.contentWindow

      iframeDocument?.App?.ExplorerGridBundle?.toolbar?.refresh()

      setRefreshMatterHistory(false)
    }

    setTimeout(() => {
      onMessageRefreshMatterHistory()
    }, 3000)

    setTimeout(() => {
      onMessageRefreshMatterHistory()
    }, 10000)
  }, [refreshMatterHistory])

  useEffect(() => {
    const fetchEmailContentFromPostMessage = async (matterHistoryId: any) => {
      try {
        await fetchEmailDetailsFromOsprey1({ matterHistoryId: matterHistoryId })
      } catch (error) {
        console.error('Error fetching email details:', error)
      }
    }

    const onMessage = async (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return
      if (!e.data?.type?.startsWith('email:')) return

      const isEmailType = !!e.data?.type?.startsWith?.('email:')
      setSuppressHeader(e.data?.type === 'email:newEmail' ? true : !isEmailType)
      setLockMatterSelection(isEmailType)

      setEmailType(
        e.data.type === 'email:newEmail'
          ? 0
          : e.data.type === 'email:reply'
            ? 1
            : e.data.type === 'email:replyAll'
              ? 2
              : e.data.type === 'email:forward'
                ? 3
                : 0
      )

      const { config } = e.data.payload

      let emailClientMatterNo: string | null = null

      if (e.data.type === 'email:newEmail') {
        const cleanClientNo = config.clientNo.indexOf('`')

        emailClientMatterNo = `${cleanClientNo === -1 ? config.clientNo : config.clientNo.slice(0, cleanClientNo)}-${config.matterNo}`

        const newOsprey1Attachment =
          config.attachMattHist && config.attachMattHist.length > 0 && config.matterHistoryRecord
            ? {
                matterHistoryContentId: config.matterHistoryRecord.MatterHistoryContentId,
                matterHistoryId: config.attachMattHist[0].matterHistoryId,
                filename: config.matterHistoryRecord.ContentName,
                contentType: config.matterHistoryRecord.Type,
                fileExtension: config.matterHistoryRecord.TypeExt,
                size: config.matterHistoryRecord.Size,
                createdDate: config.matterHistoryRecord.CreatedDate,
                createdBy: config.matterHistoryRecord.CreatedBy
              }
            : null

        newOsprey1Attachment !== null &&
          toggleSetAttachedAttachments({
            attachment: newOsprey1Attachment,
            matterHistoryContentIsConvertWordToPdfs: false
          })

        // if the item has a package link, add it to the email body
        if (config.packLink) {
          const downloadLink = `<a href="${config.packLink}">Click to download</a><br>`

          setContent(downloadLink)
          editor.commands.setContent(downloadLink)
        }

        const hasDirectEmailContent = config.subject || config.body || config.recipient

        if (hasDirectEmailContent) {
          if (config.subject) {
            setSubject(config.subject)
          }

          if (config.body && editor) {
            const bodyHtml = config.body.includes('<')
              ? config.body
              : config.body.replace(/\n/g, '<br>')
            editor.commands.setContent(bodyHtml)
            setContent(bodyHtml)
          }

          if (config.recipient && config.recipient.trim()) {
            setToRecipients({
              recipientType: 'contacts',
              name: config.recipient.trim(),
              email: config.recipient.trim()
            })
          }
        } else {
          await fetchEmailContentFromPostMessage(config.EmailClientReferenceId)
        }
      } else {
        emailClientMatterNo = `${config.config.clientNo}-${config.config.matterNo}`

        await fetchEmailContentFromPostMessage(config.config.EmailClientReferenceId)

        if (e.data.type === 'email:forward') {
          try {
            const forwardedAttachments = await fetchAttachments({
              clientMatterNo: emailClientMatterNo,
              parentMatterHistoryId: config.config.EmailClientReferenceId,
              shouldSaveToStore: false,
              startAtIndex: 0,
              pageSize: 100
            })

            const rows = forwardedAttachments?.rows ?? []

            if (Array.isArray(rows)) {
              rows.forEach((attachment: any) => {
                if (attachment.matterHistoryContentId) {
                  toggleSetAttachedAttachments({
                    attachment,
                    matterHistoryContentIsConvertWordToPdfs: false
                  })
                }
              })
            }
          } catch (error) {
            console.error('Error fetching forwarded email attachments:', error)
          }
        }
      }

      const matterExists = matters[emailClientMatterNo] ? true : false

      const fetchedMatter = matterExists
        ? matters[emailClientMatterNo]
        : await fetchMatter({ clientMatterNo: emailClientMatterNo })

      setEmailSendingCurrentMatterData({
        clientMatterNo: fetchedMatter.clientMatterNo,
        clientNo: fetchedMatter.clientNo,
        dateArchived: fetchedMatter.dateArchived,
        feeEarnerDescription: fetchedMatter.feeEarnerDescription,
        forename: fetchedMatter.forename,
        highRisk: fetchedMatter.highRisk,
        emailClientReferenceId: config.emailClientReferenceId ?? null,
        isArchived: fetchedMatter.archiveReference,
        isUserRestricted: fetchedMatter.isUserRestricted,
        matterDescription: fetchedMatter.matterDescription,
        matterNo: fetchedMatter.matterNo,
        surname: fetchedMatter.surname,
        workTypeId: fetchedMatter.workflowTypeId
      })

      const currentEmailType =
        e.data.type === 'email:newEmail'
          ? 0
          : e.data.type === 'email:reply'
            ? 1
            : e.data.type === 'email:replyAll'
              ? 2
              : e.data.type === 'email:forward'
                ? 3
                : 0

      if (currentEmailType === 1 || currentEmailType === 2 || currentEmailType === 3) {
        const { emailDetailsFromOsprey1: freshEmailDetails } = useEmailCardStore.getState()
        const internetMessageId = freshEmailDetails?.internetMessageId

        if (!internetMessageId) {
          setShowMatterHistorySettings(true)
        } else {
          try {
            const conversations = await fetchAllMatterEmailConversationsByInternetMessageIds({
              internetMessageId: internetMessageId
            })

            const hasValidConversation = conversations?.some(
              (conversation: any) =>
                conversation.emailConversationId !== null && conversation.emailConversationId !== ''
            )

            setShowMatterHistorySettings(!hasValidConversation)
          } catch (error) {
            setShowMatterHistorySettings(true)
          }
        }
      } else {
        setShowMatterHistorySettings(true)
      }

      setOpen({ isOpen: true })
    }

    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [setEmailSendingCurrentMatterData, setOpen])

  useEffect(() => {
    if (!editor) return

    const body = emailDetailsFromOsprey1?.body ?? ''
    const subj = emailDetailsFromOsprey1?.subject ?? ''
    const to = emailDetailsFromOsprey1?.to ?? []
    const cc = emailDetailsFromOsprey1?.cc ?? []
    const bcc = emailDetailsFromOsprey1?.bcc ?? []
    const from = emailDetailsFromOsprey1?.from
    const attachments = emailDetailsFromOsprey1?.attachments ?? []

    const headerHTML = buildHeaderHTML(emailDetailsFromOsprey1)

    const SPACER_LINES = 1
    const spacerHTML = Array.from({ length: SPACER_LINES })
      .map(() => '<p><br></p>')
      .join('')

    const setQuoted = (originalHTML: string) => {
      const includeHeader = !suppressHeader && headerHTML.trim() !== ''
      const finalHTML = includeHeader
        ? `${spacerHTML}${headerHTML}<span style="color:#3D517B"><blockquote>${originalHTML || '<p></p>'}</blockquote></span>`
        : `${spacerHTML}${originalHTML || '<p></p>'}`
      editor.commands.setContent(finalHTML)
      setContent(finalHTML)
      editor.commands.focus('start')
    }

    if (typeof body === 'string' && body.trim().length > 0) {
      editor.commands.setContent(body)
      const normalized = editor.getHTML()
      setQuoted(normalized)
    } else {
      const parsed = parseEditorContent(body)
      editor.commands.setContent(parsed as any)
      const normalized = editor.getHTML()
      setQuoted(normalized)
    }

    if (subj) {
      let hasCorrectThreadPrefix = emailType === 0

      if (emailType === 1 || emailType === 2) {
        hasCorrectThreadPrefix = /^\s*re(?:\s*\[\d+\])?\s*:/i.test(subj) || false
      } else if (emailType === 3) {
        hasCorrectThreadPrefix = /^\s*fw(?:d)?(?:\s*\[\d+\])?\s*:/i.test(subj) || false
      }

      setSubject(
        hasCorrectThreadPrefix
          ? subj
          : emailType === 1 || emailType === 2
            ? `Re: ${subj}`
            : emailType === 3
              ? `Fwd: ${subj}`
              : subj
      )
    }

    const norm = (v?: string) => (v ?? '').trim().toLowerCase()
    const myEmail = norm(graphApiEmailAddress)

    const filterOutMe = (arr: any[]) => (arr ?? []).filter((r) => norm(r?.emailAdresses || r?.email) !== myEmail)

    const pushRecipient = (() => {
      const seenByKind: Record<RecipientKind, Set<string>> = {
        to: new Set(),
        cc: new Set(),
        bcc: new Set()
      }

      const setterByKind: Record<RecipientKind, typeof setToRecipients> = {
        to: setToRecipients,
        cc: setCcRecipients,
        bcc: setBccRecipients
      }

      return (kind: RecipientKind, rec: any) => {
        const email: string | undefined = rec?.emailAdresses || rec?.email
        const name: string = rec?.displayName || rec?.name || ''
        const key = norm(email)

        if (!email || key === myEmail || seenByKind[kind].has(key)) return

        seenByKind[kind].add(key)
        setterByKind[kind]({
          recipientType: 'contacts',
          name,
          email
        })
      }
    })()

    if (emailType !== 3) {
      if (from) pushRecipient('to', from)

      filterOutMe(to).forEach((r) => pushRecipient('to', r))
      filterOutMe(cc).forEach((r) => pushRecipient('cc', r))
      filterOutMe(bcc).forEach((r) => pushRecipient('bcc', r))
    }
  }, [
    editor,
    emailDetailsFromOsprey1,
    emailType,
    suppressHeader,
    graphApiEmailAddress,
    setToRecipients,
    setCcRecipients,
    setBccRecipients
  ])

  useEffect(() => {
    open === true && checkForGraphAPIToken()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (open && graphApiEmailAddress === null) checkForGraphAPIToken()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, graphApiEmailAddress])

  useEffect(() => {
    const isNewEmail = emailType === 0
    if (!isNewEmail) return

    const matterSubject = emailSendingCurrentMatterData?.clientMatterNo
      ? `${emailSendingCurrentMatterData.clientMatterNo}: ${emailSendingCurrentMatterData.matterDescription}`
      : ''

    if (emailSendingCurrentMatterData?.clientMatterNo === null && subject === previousClientMatterNoRef.current) {
      setSubject('')
    } else if (subject === null || subject === '') {
      setSubject(matterSubject)
    }

    if (emailSendingCurrentMatterData?.clientMatterNo) {
      previousClientMatterNoRef.current = matterSubject
    }
  }, [emailSendingCurrentMatterData.clientMatterNo])

  const addPackageLinkToEmail = useCallback(
    (packageItem: any) => {
      const { matterHistoryPackageId } = packageItem
      const { baseURL } = useGlobalSettingsStore.getState()
      const downloadLink = `<br><a href="${baseURL}/DownloadMatterHistoryPackage.ashx?pkid=${matterHistoryPackageId}">Click to download</a>`

      const newContent = content + downloadLink
      setContent((prev) => prev + downloadLink)
      editor?.commands?.setContent(newContent)
    },
    [content, editor]
  )

  useEffect(() => {
    if (mostRecentlySelectedPackage) addPackageLinkToEmail(mostRecentlySelectedPackage)
  }, [mostRecentlySelectedPackage])

  return (
    <div data-testid="EmailCompositionPanel">
      <Modal
        open={open}
        handleClose={() => handleClose()}
        title={``}
        sx={{
          backgroundColor: '#FFF',
          maxWidth: '1400px',
          width: 'calc(100% - 60px)',
          height: 'auto',
          minHeight: 'calc(100% - 60px)',
          padding: 0,
          maxHeight: 'calc(100% - 60px)',
          display: 'flex',
          flexDirection: 'column',
          zIndex: '5000',
          [`& > .MuiStack-root`]: {
            mt: 0,
            mb: 0
          }
        }}
        theme={theme}
      >
        <MUIPaper
          sx={{
            alignItems: 'flex-start',
            background: 'white',
            borderRadius: 1,
            boxShadow: 'none',
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            height: '100%',
            justifyContent: 'flex-start',
            minHeight: '100%',
            minWidth: '100%',
            mt: 0,
            overflow: 'hidden',
            padding: '0',
            position: 'relative',
            width: '100%'
          }}
        >
          {isSendingEmail && (
            <MUIStack
              alignItems="center"
              justifyContent="center"
              gap={4}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100%',
                height: '100%',
                zIndex: 10,
                backgroundColor: 'rgba(255, 255, 255, 0.75)'
              }}
            >
              <LoadingState />
              <MUITypography variant="h6">Sending...</MUITypography>
            </MUIStack>
          )}

          <MUIStack
            direction="column"
            sx={{ width: '100%', flexGrow: 1, minHeight: '100%', pointerEvents: isSendingEmail ? 'none' : 'auto' }}
          >
            <MUIStack
              display="flex"
              direction="row"
              gap={4}
              justifyContent="flex-start"
              alignItems="center"
              sx={{
                borderBottom: `1px solid ${theme.palette.grey[300]}`,
                padding: '1rem 1.5rem',
                width: '100%',
                position: 'relative'
              }}
            >
              <MUIButton
                variant="outlined"
                disabled={isSendingEmail}
                onClick={handleSendEmail}
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  border: '0px solid transparent',
                  color: theme.palette.primary.contrastText,
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  height: '100%',
                  textTransform: '',
                  transition: 'background-color 0.3s ease-in-out',
                  width: 'fit-content',
                  '&:disabled': {
                    opacity: 0.5,
                    cursor: 'not-allowed',
                    color: 'white'
                  },
                  '&:hover': {
                    cursor: 'pointer',
                    border: '0px solid transparent',
                    backgroundColor: theme.palette.primary.dark,
                    transition: 'background-color 0.3s ease-in-out'
                  }
                }}
              >
                <SendIcon sx={{ height: '1rem', mr: 2, width: '1rem' }} />
                Send
              </MUIButton>

              <MUIStack direction="row" alignItems="center">
                <MUIInputLabel htmlFor="email-select" sx={{ marginRight: '0.5rem', width: '3rem', mb: 0 }}>
                  From:
                </MUIInputLabel>
                <MUISelect
                  id="email-select"
                  value={graphApiEmailAddress ?? ''}
                  sx={{
                    border: '0px solid transparent',
                    borderRadius: '4px',
                    maxHeight: '2rem',
                    mb: 0,
                    p: 0,
                    width: '15rem',
                    ['& .MuiSelect-select']: { p: '1rem 1rem 1rem 0 !important', maxHeight: '2rem' },
                    ['& .MuiOutlinedInput-notchedOutline']: {
                      border: 'none'
                    }
                  }}
                >
                  <MUIMenuItem key={graphApiEmailAddress} value={graphApiEmailAddress} sx={{ maxHeight: '2rem', width: '100%' }}>
                    {graphApiEmailAddress}
                  </MUIMenuItem>
                </MUISelect>
              </MUIStack>

              {
                // <EmailImportanceControl value={flagged} onChange={setFlagged} />
              }

              <MUIButton
                variant="text"
                onClick={() => handleClose()}
                sx={{ minWidth: 0, position: 'absolute', right: '1rem', pointer: 'cursor' }}
              >
                <CloseIcon sx={{ width: '1rem', height: '1rem', cursor: 'pointer' }} />
              </MUIButton>
            </MUIStack>

            <MUIStack
              direction="row"
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: 'hidden'
              }}
            >
              <div
                ref={dropZoneRef}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                style={{
                  position: 'relative',
                  width: '100%',
                  height: 'auto%',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: '100%',
                  minWidth: 0,
                  flex: 1
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    addFiles(Array.from(e.target.files ?? []))
                    if (e.currentTarget) e.currentTarget.value = ''
                  }}
                />

                <MUIStack
                  direction="column"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    overflowY: 'hidden',
                    paddingBottom: 2,
                    flexGrow: 1,
                    minHeight: '100%'
                  }}
                >
                  <MUIStack
                    direction="column"
                    alignItems="center"
                    justifyContent={'center'}
                    sx={{ width: '100%', height: 'auto', minHeight: '100%' }}
                  >
                    <RecipientsInput
                      label="To:"
                      field="to"
                      inputValue={inputToValue}
                      recipients={toRecipients}
                      removeRecipients={removeToRecipient}
                      setInputValue={setInputToValue}
                      setRecipients={setToRecipients}
                      testData={testData}
                      onValidityChange={handleValidity}
                      canSend={canSend}
                    />

                    <RecipientsInput
                      label="Cc:"
                      field="cc"
                      inputValue={inputCcValue}
                      recipients={ccRecipients}
                      removeRecipients={removeCcRecipient}
                      setInputValue={setInputCcValue}
                      setRecipients={setCcRecipients}
                      testData={testData}
                      onValidityChange={handleValidity}
                      canSend={canSend}
                    />

                    <RecipientsInput
                      label="Bcc:"
                      field="bcc"
                      inputValue={inputBccValue}
                      recipients={bccRecipients}
                      removeRecipients={removeBccRecipient}
                      setInputValue={setInputBccValue}
                      setRecipients={setBccRecipients}
                      testData={testData}
                      onValidityChange={handleValidity}
                      canSend={canSend}
                    />

                    <MUIStack
                      direction="row"
                      sx={{
                        borderBottom: `1px solid ${theme.palette.grey[300]}`,
                        margin: '1rem 1rem',
                        width: `calc(100% - 2rem)`,
                        height: '36px'
                      }}
                    >
                      <MUIInput
                        variant="standard"
                        value={subject}
                        onChange={(e: any) => setSubject(e.target.value)}
                        placeholder="Add a subject"
                        sx={{
                          borderBottom: 'none',
                          width: '100%',
                          '.MuiInputBase-root': {
                            marginBottom: 0,
                            '&:before, &:after': { display: 'none' }
                          }
                        }}
                      />
                    </MUIStack>

                    <MUIStack
                      justifyContent={'flex-start'}
                      alignItems={'flex-start'}
                      direction="row"
                      flexWrap={'wrap'}
                      sx={{
                        width: '100%',
                        borderBottom: `1px solid ${theme.palette.grey[300]}`,
                        mb: '0.5rem',
                        height: 'auto'
                      }}
                    >
                      <MUIButton
                        variant="text"
                        onClick={() => fileInputRef.current?.click()}
                        sx={{ color: theme.palette.grey[400], ml: 2, textTransform: 'unset' }}
                      >
                        <PaperClipIcon sx={{ height: '1rem', width: '1rem', mr: 2 }} />
                        Add attachments
                      </MUIButton>

                      <MUIStack direction="row" flexWrap={'wrap'} sx={{ ml: 4, mt: 0.5, gap: 1, overflow: 'hidden' }}>
                        {attachedAttachments.map((a: any) => (
                          <MUIInputLabel
                            key={`${a.matterHistoryContentId}-${a.filename}`}
                            style={{
                              backgroundColor: theme.palette.grey[300],
                              borderRadius: '1rem',
                              color: theme.palette.text.secondary,
                              marginRight: '0.5rem',
                              width: 'fit-content',
                              padding: '0.25rem 0.75rem',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                          >
                            {a.filename}
                            <MUIButton
                              onClick={() =>
                                removeAttachedAttachment({ matterHistoryContentId: a.matterHistoryContentId })
                              }
                              variant="text"
                              sx={{
                                width: '1rem',
                                height: '1rem',
                                p: 0,
                                margin: '0 0 0.125rem 0.5rem',
                                cursor: 'pointer',
                                minWidth: '1rem !important'
                              }}
                              aria-label={`Remove ${a.filename}`}
                            >
                              <CloseIcon
                                sx={{
                                  borderRadius: '50%',
                                  border: `0.5px solid ${theme.palette.text.secondary}`,
                                  height: '1rem',
                                  width: '1rem',
                                  p: '2px',
                                  mt: '2px',
                                  cursor: 'pointer',
                                  color: theme.palette.text.secondary,
                                  strokeWidth: '3px',
                                  strokeOpacity: '1.0'
                                }}
                              />
                            </MUIButton>
                          </MUIInputLabel>
                        ))}

                        {deviceAttachments.map((a) => (
                          <MUIInputLabel
                            key={a.id}
                            style={{
                              backgroundColor: theme.palette.grey[300],
                              borderRadius: '1rem',
                              color: theme.palette.text.secondary,
                              marginRight: '0.5rem',
                              width: 'fit-content',
                              padding: '0.25rem 0.75rem',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                          >
                            {a.name}
                            <MUIButton
                              onClick={() => setDeviceAttachments((prev) => prev.filter((x) => x.id !== a.id))}
                              variant="text"
                              sx={{
                                width: '1rem',
                                height: '1rem',
                                p: 0,
                                margin: '0 0 0.125rem 0.5rem',
                                cursor: 'pointer',
                                minWidth: '1rem !important'
                              }}
                              aria-label={`Remove ${a.name}`}
                            >
                              <CloseIcon
                                sx={{
                                  borderRadius: '50%',
                                  border: `0.5px solid ${theme.palette.text.secondary}`,
                                  height: '1rem',
                                  width: '1rem',
                                  p: '2px',
                                  mt: '2px',
                                  cursor: 'pointer',
                                  color: theme.palette.text.secondary,
                                  strokeWidth: '3px',
                                  strokeOpacity: '1.0'
                                }}
                              />
                            </MUIButton>
                          </MUIInputLabel>
                        ))}
                      </MUIStack>
                    </MUIStack>

                    <MUIStack
                      spacing={1}
                      sx={{
                        width: '100%',
                        maxHeight: '100%',
                        minHeight: '200px',
                        height: 'auto',
                        flexGrow: 1
                      }}
                    >
                      <MUIStack direction="row" spacing={1} flexWrap={'wrap'}>
                        <MUITooltip
                          placement="bottom"
                          arrow
                          sx={{ cursor: 'pointer', backgroundColor: '#1c3146' }}
                          title={'Bold'}
                        >
                          <MUIIconButton
                            onClick={() => editor?.chain().focus().toggleBold().run()}
                            disabled={!editor?.can().chain().focus().toggleBold().run()}
                            sx={{ backgroundColor: 'transparent' }}
                          >
                            <FormatBold />
                          </MUIIconButton>
                        </MUITooltip>
                        <MUITooltip
                          placement="bottom"
                          arrow
                          sx={{ cursor: 'pointer', backgroundColor: '#1c3146' }}
                          title={'Italic'}
                        >
                          <MUIIconButton
                            onClick={() => editor?.chain().focus().toggleItalic().run()}
                            disabled={!editor?.can().chain().focus().toggleItalic().run()}
                            sx={{ backgroundColor: 'transparent' }}
                          >
                            <FormatItalic />
                          </MUIIconButton>
                        </MUITooltip>
                        <MUITooltip
                          placement="bottom"
                          arrow
                          sx={{ cursor: 'pointer', backgroundColor: '#1c3146' }}
                          title={'Underline'}
                        >
                          <MUIIconButton
                            onClick={() => editor?.chain().focus().toggleUnderline().run()}
                            disabled={!editor?.can().chain().focus().toggleUnderline().run()}
                            sx={{ backgroundColor: 'transparent' }}
                          >
                            <FormatUnderlined />
                          </MUIIconButton>
                        </MUITooltip>
                        <MUITooltip
                          placement="bottom"
                          arrow
                          sx={{ cursor: 'pointer', backgroundColor: '#1c3146' }}
                          title={'Strikethrough'}
                        >
                          <MUIIconButton
                            onClick={() => editor?.chain().focus().toggleStrike().run()}
                            disabled={!editor?.can().chain().focus().toggleStrike().run()}
                            sx={{ backgroundColor: 'transparent' }}
                          >
                            <FormatStrikethrough />
                          </MUIIconButton>
                        </MUITooltip>
                        <MUITooltip
                          placement="bottom"
                          arrow
                          sx={{ cursor: 'pointer', backgroundColor: '#1c3146' }}
                          title={'Highlight'}
                        >
                          <MUIIconButton
                            onClick={() => editor?.chain().focus().toggleHighlight({ color: '#FFFF00' }).run()}
                            disabled={!editor?.can().chain().focus().toggleHighlight({ color: '#FFFF00' }).run()}
                            sx={{ backgroundColor: 'transparent' }}
                          >
                            <FormatHighlight />
                          </MUIIconButton>
                        </MUITooltip>
                        <MUITooltip
                          placement="bottom"
                          arrow
                          sx={{ cursor: 'pointer', backgroundColor: '#1c3146' }}
                          title={'Link'}
                        >
                          <MUIIconButton
                            onClick={(e: any) => setLinkAnchorEl(e.currentTarget)}
                            sx={{ backgroundColor: 'transparent' }}
                          >
                            <FormatLink />
                          </MUIIconButton>
                        </MUITooltip>
                        <MUITooltip
                          placement="bottom"
                          arrow
                          sx={{ cursor: 'pointer', backgroundColor: '#1c3146' }}
                          title={'Subscript'}
                        >
                          <MUIIconButton
                            onClick={() => editor?.chain().focus().toggleSubscript().run()}
                            disabled={!editor?.can().chain().focus().toggleSubscript().run()}
                            sx={{ backgroundColor: 'transparent' }}
                          >
                            <FormatSubscript />
                          </MUIIconButton>
                        </MUITooltip>
                        <MUITooltip
                          placement="bottom"
                          arrow
                          sx={{ cursor: 'pointer', backgroundColor: '#1c3146' }}
                          title={'Superscript'}
                        >
                          <MUIIconButton
                            onClick={() => editor?.chain().focus().toggleSuperscript().run()}
                            disabled={!editor?.can().chain().focus().toggleSuperscript().run()}
                            sx={{ backgroundColor: 'transparent' }}
                          >
                            <FormatSuperscript />
                          </MUIIconButton>
                        </MUITooltip>
                        <MUITooltip
                          placement="bottom"
                          arrow
                          sx={{ cursor: 'pointer', backgroundColor: '#1c3146' }}
                          title={'Align Left'}
                        >
                          <MUIIconButton
                            onClick={() => editor?.chain().focus().setTextAlign('left').run()}
                            disabled={!editor?.can().chain().focus().setTextAlign('left').run()}
                            sx={{ backgroundColor: 'transparent' }}
                          >
                            <FormatAlignLeft />
                          </MUIIconButton>
                        </MUITooltip>
                        <MUITooltip
                          placement="bottom"
                          arrow
                          sx={{ cursor: 'pointer', backgroundColor: '#1c3146' }}
                          title={'Align Centre'}
                        >
                          <MUIIconButton
                            onClick={() => editor?.chain().focus().setTextAlign('center').run()}
                            disabled={!editor?.can().chain().focus().setTextAlign('center').run()}
                            sx={{ backgroundColor: 'transparent' }}
                          >
                            <FormatAlignCenter />
                          </MUIIconButton>
                        </MUITooltip>
                        <MUITooltip
                          placement="bottom"
                          arrow
                          sx={{ cursor: 'pointer', backgroundColor: '#1c3146' }}
                          title={'Align Right'}
                        >
                          <MUIIconButton
                            onClick={() => editor?.chain().focus().setTextAlign('right').run()}
                            disabled={!editor?.can().chain().focus().setTextAlign('right').run()}
                            sx={{ backgroundColor: 'transparent' }}
                          >
                            <FormatAlignRight />
                          </MUIIconButton>
                        </MUITooltip>
                        <MUITooltip
                          placement="bottom"
                          arrow
                          sx={{ cursor: 'pointer', backgroundColor: '#1c3146' }}
                          title={'Bullet List'}
                        >
                          <MUIIconButton
                            onClick={() => editor?.chain().focus().toggleBulletList().run()}
                            sx={{ backgroundColor: 'transparent' }}
                          >
                            <FormatListBulleted />
                          </MUIIconButton>
                        </MUITooltip>
                        <MUITooltip
                          placement="bottom"
                          arrow
                          sx={{ cursor: 'pointer', backgroundColor: '#1c3146' }}
                          title={'Numbered List'}
                        >
                          <MUIIconButton
                            onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                            sx={{ backgroundColor: 'transparent' }}
                          >
                            <FormatListNumbered />
                          </MUIIconButton>
                        </MUITooltip>
                        <MUITooltip
                          placement="bottom"
                          arrow
                          sx={{ cursor: 'pointer', backgroundColor: '#1c3146' }}
                          title={'Table'}
                        >
                          <MUIIconButton
                            onClick={(e: any) => setTableAnchorEl(e.currentTarget)}
                            sx={{ backgroundColor: 'transparent' }}
                          >
                            <TableChart />
                          </MUIIconButton>
                        </MUITooltip>
                        <MUITooltip
                          placement="bottom"
                          arrow
                          sx={{ cursor: 'pointer', backgroundColor: '#1c3146' }}
                          title={'Undo'}
                        >
                          <MUIIconButton
                            onClick={() => editor?.chain().focus().undo().run()}
                            sx={{ backgroundColor: 'transparent' }}
                          >
                            <Undo />
                          </MUIIconButton>
                        </MUITooltip>
                        <MUITooltip
                          placement="bottom"
                          arrow
                          sx={{ cursor: 'pointer', backgroundColor: '#1c3146' }}
                          title={'Redo'}
                        >
                          <MUIIconButton
                            onClick={() => editor?.chain().focus().redo().run()}
                            sx={{ backgroundColor: 'transparent' }}
                          >
                            <Redo />
                          </MUIIconButton>
                        </MUITooltip>
                      </MUIStack>

                      <MUIPaper
                        variant="outlined"
                        sx={{
                          '&': {
                            '--gray-1': theme.palette.grey[100],
                            '--gray-2': theme.palette.grey[200],
                            '--gray-3': theme.palette.grey[300],
                            '--main': theme.palette.primary.main
                          },
                          backgroundColor: '#FFF',
                          fontFamily: theme.typography.fontFamily,
                          p: '10px 20px',
                          width: '100%',
                          minHeight: 'auto',
                          border: `none`,
                          borderRadius: 0,
                          boxShadow: 'none',
                          overflowY: 'scroll',
                          height: 'auto',
                          flexGrow: 1,
                          '& > div': {
                            height: 'auto',
                            minHeight: '200px'
                          },
                          '& .ProseMirror': {
                            outline: 'none',
                            minHeight: '150px',
                            fontFamily: 'inherit',
                            fontSize: '1rem'
                          }
                        }}
                      >
                        <EditorContent editor={editor} className={styles.tiptap} />
                      </MUIPaper>
                    </MUIStack>
                  </MUIStack>

                  {isDragging && (
                    <MUIPaper
                      elevation={0}
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        border: '2px dashed',
                        borderColor: theme.palette.primary.main,
                        bgcolor: 'rgba(0,0,0,0.04)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                        zIndex: 10
                      }}
                    >
                      <MUIStack alignItems="center" spacing={1}>
                        <PaperClipIcon sx={{ width: '1.25rem', height: '1.25rem' }} />
                        <MUITypography variant="body1">Drop files to attach</MUITypography>
                      </MUIStack>
                    </MUIPaper>
                  )}
                </MUIStack>
              </div>

              <EmailSidebar
                testData={testData}
                lockMatterSelection={lockMatterSelection}
                showMatterHistorySettings={showMatterHistorySettings}
              />
            </MUIStack>
          </MUIStack>
        </MUIPaper>

        <MUIPopover
          open={Boolean(linkAnchorEl)}
          anchorEl={linkAnchorEl}
          onClose={closeLinkPopover}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <MUIStack sx={{ p: 2, minWidth: 320 }} spacing={1.5}>
            <MUIInput
              label="Text (optional)"
              variant="standard"
              value={linkText}
              onChange={(e: any) => setLinkText(e.target.value)}
            />
            <MUIInput
              label="URL"
              variant="standard"
              value={linkUrl}
              onChange={(e: any) => setLinkUrl(e.target.value)}
              placeholder="https://example.com"
            />
            <MUIFormControlLabel
              control={<MUICheckbox checked={linkNewTab} onChange={(e) => setLinkNewTab(e.target.checked)} />}
              label="Open in new tab"
            />
            <MUIDivider />
            <MUIStack direction="row" justifyContent="flex-end" spacing={1}>
              <MUIButton onClick={closeLinkPopover}>Cancel</MUIButton>
              <MUIButton variant="contained" onClick={handleApplyLink} disabled={!linkUrl.trim()}>
                Apply link
              </MUIButton>
            </MUIStack>
          </MUIStack>
        </MUIPopover>

        <MUIPopover
          open={Boolean(openTablePopover)}
          anchorEl={tableAnchorEl}
          onClose={closeTablePopover}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <MUIStack sx={{ p: 2, minWidth: 280 }} spacing={1.5}>
            <MUIStack direction="row" spacing={1.5}>
              <MUIInput
                label="Rows"
                variant="standard"
                type="number"
                value={tableRows}
                inputProps={{ min: 1, max: 50 }}
                onChange={(e: any) => setTableRows(Number(e.target.value))}
                sx={{ width: '100%' }}
              />
              <MUIInput
                label="Columns"
                variant="standard"
                type="number"
                value={tableCols}
                inputProps={{ min: 1, max: 50 }}
                onChange={(e: any) => setTableCols(Number(e.target.value))}
                sx={{ width: '100%' }}
              />
            </MUIStack>

            <MUIFormControlLabel
              control={<MUICheckbox checked={tableHeaderRow} onChange={(e) => setTableHeaderRow(e.target.checked)} />}
              label="Header row"
            />

            <MUIDivider />
            <MUIStack direction="row" justifyContent="flex-end" spacing={1}>
              <MUIButton onClick={closeTablePopover}>Cancel</MUIButton>
              <MUIButton
                variant="contained"
                onClick={handleInsertTable}
                disabled={(Number(tableRows) || 0) < 1 || (Number(tableCols) || 0) < 1}
              >
                Insert table
              </MUIButton>
            </MUIStack>
          </MUIStack>
        </MUIPopover>
      </Modal>
    </div>
  )
}
